import { Component, inject, computed, OnInit, OnDestroy, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription, filter } from 'rxjs';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { FullHeightComponent } from './shared/components/full-height-component/full-height.component';
import { UserService } from './core/services/user.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { debounceTime } from 'rxjs';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { AuthService } from './core/services/auth.service';
import { AccountService } from './core/services/account.service';
import { CompanyService } from './core/services/company.service';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { SuperAdminSessionService } from './core/services/super-admin-session.service';
import { ActiveRoleService } from './core/services/active-role.service';
import { SocketService } from './core/services/socket.service';
import { LiveToastsComponent } from './shared/components/live-toasts/live-toasts.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LoadManagementDemoDialogComponent } from './shared/components/load-management-demo-dialog/load-management-demo-dialog.component';
import { environment } from '../environments/environment';
import { SessionHeartbeatService } from './core/services/session-heartbeat.service';
import { IdleService } from './core/services/idle.service';

interface ToastSocketPayload {
  message?: string;
  type?: string; // legacy 'type'
  severity?: string; // preferred field
  code?: string;
  meta?: Record<string, unknown>;
  ts?: string | number;
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, MatProgressSpinnerModule, MatProgressBarModule, LiveToastsComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent extends FullHeightComponent implements OnInit, OnDestroy {
  title = 'e2x-web';

  private userService = inject(UserService);
  private authService = inject(AuthService);
  private companyService = inject(CompanyService);
  private accountService = inject(AccountService);
  private socketService = inject(SocketService);
  private superAdminSession = inject(SuperAdminSessionService);
  private activeRoleService = inject(ActiveRoleService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private readonly sessionHeartbeat = inject(SessionHeartbeatService);
  private readonly idleService = inject(IdleService);

  private readonly enableLiveProofs = environment.features?.enableLiveProofs ? false : false;
  readonly showLiveProofs = this.enableLiveProofs;

  // E2X-2035: indeterminate progress bar while the router is busy (lazy chunk
  // load, guard awaits, etc). Surfaces "something is happening" between
  // clicking Continue and seeing the destination page.
  private readonly router = inject(Router);
  readonly isNavigating = signal(false);
  private readonly navSub = this.router.events
    .pipe(
      filter(
        e =>
          e instanceof NavigationStart ||
          e instanceof NavigationEnd ||
          e instanceof NavigationCancel ||
          e instanceof NavigationError,
      ),
    )
    .subscribe(e => this.isNavigating.set(e instanceof NavigationStart));

  /**
   * On component initialization, enforce SuperAdmin routing rules
   * If user is SuperAdmin, they should be automatically routed to SuperAdmin dashboard
   */
  private toastSub?: Subscription;
  private traceSub?: Subscription;
  private loadManagementDialog?: MatDialogRef<LoadManagementDemoDialogComponent, string>;

  ngOnInit(): void {
    // Check if authenticated before enforcing rules
    if (this.authService.isAuthenticated()) {
      // Enforce SuperAdmin routing rules - this will redirect to /admin2/dashboard if needed
      void this.superAdminSession.enforceRoutingRules();
    }
    this.traceSub = this.socketService
      .onEvent<Record<string, unknown>>('TraceEvent')
      .subscribe(msg => console.log('Received TraceEvent in AppComponent:', msg));

    // Listen for 'toast' events from the backend and show a snackbar
    if (this.enableLiveProofs) {
      this.toastSub = this.socketService.onEvent<ToastSocketPayload>('toast').subscribe(payload => {
        console.log('AppComponent: Received toast event from socket:', payload);
        if (!payload) return;
        const message = payload.message?.trim() || 'Notification';
        // Normalize severity: prefer explicit severity -> fallback to legacy type -> info
        const sevRaw = (payload.severity || payload.type || 'info').toLowerCase();
        const allowed = ['info', 'success', 'warning', 'error'];
        const severity = allowed.includes(sevRaw) ? sevRaw : 'info';
        console.log('AppComponent: Showing toast notification:', { message, severity });
        this.snackBar.open(message, undefined, {
          panelClass: [`toast-${severity}`],
          duration: 5000,
        });

        if (this.isLoadManagementToast(payload, message.toLowerCase())) {
          this.openLoadManagementDialog({
            message,
            severity,
            code: payload.code,
            meta: payload.meta,
          });
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.toastSub?.unsubscribe?.();
    this.traceSub?.unsubscribe?.();
    this.loadManagementDialog?.close();
  }

  private isLoadManagementToast(payload: ToastSocketPayload, normalizedMessage: string): boolean {
    if (
      payload.code &&
      ['load_check_complete', 'load_check_failed', 'load_check_started'].includes(payload.code)
    ) {
      return true;
    }
    return (
      normalizedMessage.includes('load management') ||
      normalizedMessage.includes('load check') ||
      normalizedMessage.includes('lmt job')
    );
  }

  private openLoadManagementDialog(data: {
    message: string;
    severity: string;
    code?: string;
    meta?: Record<string, unknown>;
  }): void {
    if (!this.enableLiveProofs) {
      return;
    }
    this.loadManagementDialog?.close();
    this.loadManagementDialog = this.dialog.open(LoadManagementDemoDialogComponent, {
      width: '420px',
      disableClose: false,
      data: {
        title: 'Load Management Task',
        message: data.message,
        severity: data.severity as 'success' | 'info' | 'warning' | 'error',
        code: data.code,
        meta: data.meta ?? null,
      },
    });

    this.loadManagementDialog.afterClosed().subscribe(() => {
      this.loadManagementDialog = undefined;
    });
  }

  readonly userQuery = this.userService.userQuery;
  readonly companiesQuery = injectQuery(() => ({
    ...this.companyService.getAllCompaniesQueryOptions(),
    // E2X-2035: platform-wide company list is super-admin only.
    enabled:
      this.authService.isAuthenticated() &&
      this.activeRoleService.getActiveRole() === 'super_admin',
  }));
  readonly accountsQuery = injectQuery(() => ({
    ...this.accountService.getAccountByCompanyIdQueryOptions(
      `${this.companiesQuery.data()?.data?.[0]?.id}`,
    ),
    enabled: this.authService.isAuthenticated() && this.companiesQuery.data()?.data?.length === 1,
  }));

  debouncedUserQueryIsPending = toSignal(
    toObservable(
      computed(() => {
        const isAuthenticated = this.authService.isAuthenticated();
        if (!isAuthenticated) {
          return false;
        }
        // E2X-2035: show the global boot spinner only on the initial profile load.
        // `isLoading()` is true iff the query is pending AND actively fetching — it
        // flips to false on success, error, or disabled, so it won't pin the spinner.
        return this.userQuery.isLoading();
      }),
    ).pipe(debounceTime(1000)),
    { initialValue: true },
  );
}
